"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  type Locale,
  type Dictionary,
  dictionaries,
  defaultLocale,
  isLocale,
  translate,
  LANG_COOKIE,
  LANG_COOKIE_MAX_AGE,
  locales,
  localeNames,
  localeFlags,
} from "@/lib/i18n";

type I18nContextValue = {
  locale: Locale;
  setLocale: (l: Locale, persistToServer?: boolean) => void;
  t: (key: string, params?: Record<string, string | number>) => string;
  dict: Dictionary;
  locales: Locale[];
  localeNames: Record<Locale, string>;
  localeFlags: Record<Locale, string>;
};

const I18nContext = createContext<I18nContextValue | null>(null);

function readCookie(name: string): string | null {
  if (typeof document === "undefined") return null;
  const match = document.cookie.match(
    new RegExp("(?:^|; )" + name + "=([^;]*)")
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAge: number) {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=${encodeURIComponent(
    value
  )}; path=/; max-age=${maxAge}; samesite=lax`;
}

export function I18nProvider({
  initialLocale,
  children,
}: {
  initialLocale?: Locale;
  children: React.ReactNode;
}) {
  const [locale, setLocaleState] = useState<Locale>(
    initialLocale && isLocale(initialLocale) ? initialLocale : defaultLocale
  );

  // On mount, pick up the persisted locale from the cookie if the server didn't
  // pass one explicitly (it usually does via the layout, but this is a safety
  // net and also covers the very first render before hydration).
  useEffect(() => {
    if (initialLocale && isLocale(initialLocale)) return;
    const fromCookie = readCookie(LANG_COOKIE);
    if (fromCookie && isLocale(fromCookie)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocaleState(fromCookie);
    }
  }, [initialLocale]);

  const setLocale = useCallback(
    (l: Locale, persistToServer = true) => {
      setLocaleState(l);
      writeCookie(LANG_COOKIE, l, LANG_COOKIE_MAX_AGE);
      if (persistToServer) {
        // Fire-and-forget preference sync when logged in.
        fetch("/api/auth/preferences", {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ preferredLang: l }),
          credentials: "include",
        }).catch(() => {});
      }
    },
    []
  );

  const t = useCallback(
    (key: string, params?: Record<string, string | number>) =>
      translate(locale, key, params),
    [locale]
  );

  const value = useMemo<I18nContextValue>(
    () => ({
      locale,
      setLocale,
      t,
      dict: dictionaries[locale],
      locales,
      localeNames,
      localeFlags,
    }),
    [locale, setLocale, t]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n(): I18nContextValue {
  const ctx = useContext(I18nContext);
  if (!ctx) {
    throw new Error("useI18n must be used inside <I18nProvider>");
  }
  return ctx;
}

export function useT() {
  return useI18n().t;
}
